import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { Container } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import OverlayReelSet from "../OverlayReelSet/OverlayReelSet";
import LineWinTextReelSet from "../LineWinTextReelSet/LineWinTextReelSet";
import ReelFront from "../../../Components/Animations/ReelFront/ReelFront";
import ReelFrameDecoration from "../../../Components/Animations/ReelFrameDecoration/ReelFrameDecoration";
import ReelBack from "../../../Components/Animations/ReelBack/ReelBack";
import WinLinesContainer from "../../Shared/WinLinesReelSet/WinLinesContainer";
import DimReelSet from "../../Shared/DimReelSet/DimReelSet";
// import DividerReelset from "../../Shared/DividerReelset/DividerReelset";
import ReelSet from "../ReelSet/ReelSet";
// import WinLines from "../../../Components/UserInterface/WinLines";
import InfoOverlay from "../../Shared/InfoOverlay/InfoOverlay";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
// import { useSelector } from "react-redux";
// import { selectFreeSpinsActive } from "../../../State/Slices/freeSpinsSlice";
// import FreeSpinGameContainer from "../../FreeSpin/FreeSpinGameContainer/FreeSpinGameContainer";
import TensionReelSet from "../TensionReelSet/TensionReelSet";
import HoldAndWinReelSet from "../../HoldAndWin/HoldAndWinReelSet";
import HoldAndWinOverlayReelSet from "../../HoldAndWin/HoldAndWinOverlayReelSet";
import HoldAndWinTensionOverlaySet from "../../HoldAndWin/HoldAndWinTensionOverlaySet";
import { useSelector } from "react-redux";
import { selectHoldAndWinActive } from "../../../State/selectors";
import { createTensionShakeAnimation } from "../../../utils/animations";
import anime from "animejs";
import HoldAndWinCounter from "../../../Components/Animations/HoldAndWinCounter/HoldAndWinCounter";
import HoldAndWinProgressBar from "../../../Components/Animations/HoldAndWinProgressBar/HoldAndWinProgressBar";
import HoldAndWinCollectorDisplay from "../../../Components/Animations/HoldAndWinCollectorDisplay/HoldAndWinCollectorDisplay";
import HoldAndWinCoinCollect from "../../../Components/Animations/HoldAndWinCoinCollect/HoldAndWinCoinCollect";

const GameContainer = React.forwardRef((props, ref) => {
  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    dividerDimensions,
    reelSpacing,
    orientation,
  } = useDimensions();

  const {
    dimReelSetRef,
    gameContainerRef,
    backgroundAnimationControllerRef,
    winLinesContainerRef,
    holdAndWinCollectorDisplayRef,
    holdAndWinCoinCollectRef,
    reelFrameDecorationRef,
  } = useReelRefs();
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  // const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null); // Inner container for shakeable elements
  const tensionShakeAnimationRef = useRef(null);

  // Calculate y position based on Hold & Win state
  const containerY = dimensions.height / 2 - symbolHeight * 0.325; // Lifted when not active



  // Play tension shake animation
  const playTensionShake = useCallback(() => {
    if (
      reelContainerRef.current &&
      symbolHeight &&
      !tensionShakeAnimationRef.current
    ) {
      tensionShakeAnimationRef.current = createTensionShakeAnimation(
        reelContainerRef.current,
        symbolHeight
      );

      // Play base_event intro animation, then transition to loop
      backgroundAnimationControllerRef.current?.playBaseEventIn?.();
    }
  }, [symbolHeight, backgroundAnimationControllerRef]);

  // Stop tension shake animation smoothly
  // forceStop parameter allows collection to stop the shake even during Hold and Win
  const stopTensionShake = useCallback((forceStop = false) => {
    // CRITICAL: Don't stop shake during Hold and Win UNLESS forceStop is true
    // This allows the shake to persist across spins but still be stopped when collection starts
    if (holdAndWinActive && !forceStop) {
      return;
    }

    if (tensionShakeAnimationRef.current) {
      tensionShakeAnimationRef.current.pause();
      tensionShakeAnimationRef.current = null;

      // Smoothly revert to original position (reel container, not main container)
      if (reelContainerRef.current) {
        anime({
          targets: reelContainerRef.current,
          x: 0,
          y: 0,
          rotate: 0,
          duration: 600,
          easing: "easeOutQuad",
        });
      }

      // Play base_event exit animation (idle continues on track 0)
      if (backgroundAnimationControllerRef.current?.playBaseEventOut) {
        backgroundAnimationControllerRef.current.playBaseEventOut();
      }
    }
  }, [
    holdAndWinActive,
    backgroundAnimationControllerRef,
  ]);

  // Expose play/stop methods via ref
  useImperativeHandle(
    gameContainerRef,
    () => ({
      playTensionShake,
      stopTensionShake,
    }),
    [playTensionShake, stopTensionShake]
  );

  // Update position when dimensions change and stop animation if active
  useEffect(() => {
    if (containerRef.current) {
      // Update container position when Hold & Win state changes
      containerRef.current.y = containerY;

      // If animation is running, stop it and restart with new position
      // BUT: Don't restart during Hold and Win - let it continue until collection
      if (tensionShakeAnimationRef.current && !holdAndWinActive) {
        stopTensionShake();
        // Restart animation with new position (only for base game)
        playTensionShake();
      }
    }
  }, [
    dimensions.width,
    dimensions.height,
    symbolHeight,
    holdAndWinActive,
    containerY,
    playTensionShake,
    stopTensionShake,
  ]);

  return (
    <Container
      ref={containerRef}
      name="game_container"
      x={dimensions.width / 2}
      y={containerY}
      sortableChildren
      eventMode="passive"
    >
      {/* Inner container for shakeable elements (reels, symbols, etc.) */}
      <Container
        ref={reelContainerRef}
        name="reel_container"
        x={0}
        y={0}
        sortableChildren
        eventMode="passive"
      >
        <ReelBack
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dimensions={dimensions}
          dividerDimensions={dividerDimensions}
        />
        {/* <WinLines
          side={"left"}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
        /> */}

        <ReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          reelSpacing={reelSpacing}
        />

        {holdAndWinActive && <HoldAndWinReelSet />}

        {holdAndWinActive && (
          <HoldAndWinOverlayReelSet
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        )}

        {holdAndWinActive && <HoldAndWinTensionOverlaySet />}

        {/* <WinLines
          side={"right"}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
        /> */}

        <DimReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          ref={dimReelSetRef}
        />

        <ReelFront
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dimensions={dimensions}
          dividerDimensions={dividerDimensions}
        />

        {/* Reel Frame Decorations (claws) - Only during Hold and Win */}
        {holdAndWinActive && (
          <ReelFrameDecoration
            ref={reelFrameDecorationRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            dimensions={dimensions}
            dividerDimensions={dividerDimensions}
            orientation={orientation}
          />
        )}

        {/* <DividerReelset
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          reelSpacing={reelSpacing}
        /> */}

        <OverlayReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
        />

        <LineWinTextReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
        />

        {FEATURES_CONFIG.WIN_LINES.ENABLED && (
          <WinLinesContainer
            ref={winLinesContainerRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
          />
        )}

        {FEATURES_CONFIG.TENSION.ENABLED && !holdAndWinActive && (
          <TensionReelSet />
        )}

        <InfoOverlay />
      </Container>

      {/* Static elements (not affected by shake) */}
      {holdAndWinActive && <HoldAndWinProgressBar />}
      {holdAndWinActive && <HoldAndWinCounter />}
      {holdAndWinActive && (
        <HoldAndWinCollectorDisplay ref={holdAndWinCollectorDisplayRef} />
      )}
      {holdAndWinActive && (
        <HoldAndWinCoinCollect ref={holdAndWinCoinCollectRef} />
      )}
      {/* {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinGameContainer />} */}
    </Container>
  );
});

GameContainer.displayName = "GameContainer";

export default GameContainer;
